import api from "./api";

const MAX_VIDEO_SEC = 60;
const MAX_VIDEO_MB = 200;

export const videoDuration = (file) =>
  new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration); };
    v.onerror = () => resolve(null); // e.g. HEVC on desktop Chrome – the server checks again
    v.src = URL.createObjectURL(file);
  });

export async function checkVideo(file) {
  const okType = /video\/(mp4|quicktime|x-m4v)/.test(file.type) || /\.(mp4|mov|m4v)$/i.test(file.name);
  if (!okType) return "Filtypen understøttes ikke – brug en MP4- eller MOV-video.";
  if (file.size > MAX_VIDEO_MB * 1024 * 1024) return "Videoen er for stor – højst 200 MB.";
  const d = await videoDuration(file);
  if (d && d > MAX_VIDEO_SEC + 0.5) return "Videoen må højst vare 1 minut.";
  return null;
}

export async function shrinkImage(file, max = 2048) {
  const img = new Image();
  img.src = URL.createObjectURL(file);
  await img.decode();
  const s = Math.min(1, max / Math.max(img.width, img.height));
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * s);
  c.height = Math.round(img.height * s);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  URL.revokeObjectURL(img.src);
  return new Promise((r) => c.toBlob((b) => r(b), "image/jpeg", 0.86));
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Chunked, resumable upload to our backend. onProgress(0..1). Returns the media object.
 *  If the server lost the session (404), the upload silently starts over once. */
export async function uploadMedia(blob, kind, filename, onProgress) {
  try {
    return await uploadOnce(blob, kind, filename, onProgress);
  } catch (err) {
    if (err.response?.status !== 404) throw err;
    onProgress?.(0);
    return uploadOnce(blob, kind, filename, onProgress);
  }
}

async function complete(id) {
  for (let i = 0; ; i++) {
    try {
      return (await api.post(`/uploads/${id}/complete`, null, { timeout: 300000 })).data;
    } catch (err) {
      const s = err.response?.status;
      const busy = s === 409 && /behandles/.test(err.response?.data?.error || "");
      if (i >= 4 || !(busy || !err.response || s >= 500)) throw err; // processing elsewhere / network: retry (complete is idempotent)
      await sleep(3000 * (i + 1));
    }
  }
}

async function uploadOnce(blob, kind, filename, onProgress) {
  const init = await api.post("/uploads", { kind, size: blob.size, content_type: blob.type || "", filename });
  const { id, chunk_size } = init.data;
  let offset = 0;
  let fails = 0;
  while (offset < blob.size) {
    const part = blob.slice(offset, offset + chunk_size);
    try {
      const r = await api.put(`/uploads/${id}?offset=${offset}`, part, {
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (e) => onProgress?.((offset + (e.loaded || 0)) / blob.size),
        timeout: 120000,
      });
      offset = r.data.received;
      fails = 0;
    } catch (err) {
      if (err.response?.status === 409) { offset = err.response.data.received; continue; }
      if (err.response && err.response.status < 500) throw err;
      if (++fails > 6) throw err; // slow/unstable network: wait, ask where we are, resume
      await sleep(1000 * 2 ** fails);
      try { offset = (await api.get(`/uploads/${id}`)).data.received; } catch { /* retry */ }
    }
    onProgress?.(offset / blob.size);
  }
  return complete(id);
}
